import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase/config";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { createSlug } from "./utils/slugUtils";

const NewsDetailScreen = () => {
  const { slug } = useParams();
  console.log("Article slug from URL:", slug);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const newsRef = ref(db, "news");
    const unsubscribe = onValue(newsRef, (snapshot) => {
      if (snapshot.exists()) {
        const newsData = snapshot.val();
        let foundArticle = null;
        let articleId = null;

        Object.entries(newsData).forEach(([id, data]) => {
          const articleSlug = createSlug(data.title);
          if (articleSlug === slug) {
            foundArticle = { id, ...data };
            articleId = id;
          }
        });

        if (foundArticle) {
          setArticle(foundArticle);
          console.log("Article data:", foundArticle);
          fetchRelatedArticles(articleId);
        } else {
          console.log("No article found with slug:", slug);
        }
      } else {
        console.log("No news data available");
      }
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchRelatedArticles = (currentId) => {
    const newsRef = ref(db, "news");
    onValue(newsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const articlesArray = Object.entries(data)
          .map(([key, value]) => ({
            id: key,
            ...value,
            slug: createSlug(value.title),
          }))
          .filter((item) => item.id !== currentId)
          .sort((a, b) => b.createdAt - a.createdAt)
          .slice(0, 3);

        setRelatedArticles(articlesArray);
      }
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Không tìm thấy bài viết</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.title} | AutoWork</title>
        <meta name="description" content={article.description} />
        <meta
          name="keywords"
          content={`${article.title}, tin tức marketing, ${
            article.category || "tin tức"
          }, autowork`}
        />
        <meta property="og:title" content={`${article.title} | AutoWork`} />
        <meta property="og:description" content={article.description} />
        <meta property="og:type" content="article" />
        {article.imageUrl && (
          <meta property="og:image" content={article.imageUrl} />
        )}
        <meta property="og:url" content={`https://autowork.vn/news/${slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} | AutoWork`} />
        <meta name="twitter:description" content={article.description} />
        {article.imageUrl && (
          <meta name="twitter:image" content={article.imageUrl} />
        )}
        <link rel="canonical" href={`https://autowork.vn/news/${slug}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.description,
            image: article.imageUrl,
            datePublished: new Date(article.createdAt).toISOString(),
            dateModified: new Date(article.createdAt).toISOString(),
            author: {
              "@type": "Organization",
              name: "AutoWork",
            },
            publisher: {
              "@type": "Organization",
              name: "AutoWork",
              logo: {
                "@type": "ImageObject",
                url: "https://autowork.vn/logo.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://autowork.vn/news/${slug}`,
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 px-4 sm:px-6 lg:px-8">
        <article className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          {article.imageUrl && (
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          )}

          <div className="p-6 md:p-8">
            <div className="text-sm text-gray-500 mb-4">
              {formatDate(article.createdAt)}
            </div>

            <div className="prose prose-lg max-w-none">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(article.content),
                }}
              />
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <div className="max-w-4xl mx-auto mt-12 pb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Bài viết gần đây
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <a
                  key={relatedArticle.id}
                  href={`/news/${relatedArticle.slug}`}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {relatedArticle.imageUrl && (
                    <img
                      src={relatedArticle.imageUrl}
                      alt={relatedArticle.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-4">
                    <div className="text-sm text-gray-500 mb-2">
                      {formatDate(relatedArticle.createdAt)}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                      {relatedArticle.title}
                    </h3>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NewsDetailScreen;
